import type { Role } from '@sentra/contracts';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { sectionsFor } from '@/app/navigation';
import { Separator } from '@/design/components/separator';
import { cn } from '@/design/utils';

/**
 * The section list.
 *
 * **A real `<nav>` with a real list of links**, so a screen reader announces how many sections there
 * are and a keyboard reaches each one with Tab in the order they are drawn. The active one carries
 * `aria-current="page"`, which is the only correct way to say "you are here" — a colour alone says
 * it to nobody who cannot see it.
 *
 * **Nothing here decides authorization.** A section the operator's role does not reach is simply
 * not offered; the server checks every route twice regardless (AD-36).
 */
export function Sidebar({
  role,
  onNavigate,
}: Readonly<{ role: Role; onNavigate?: () => void }>): ReactNode {
  const { t } = useTranslation();

  return (
    <nav aria-label={t('shell.navigationLabel')} className="flex h-full flex-col gap-1 p-3">
      <div className="px-2 py-3">
        <span className="text-lg font-semibold tracking-tight">{t('app.name')}</span>
      </div>
      <Separator className="mb-2" />
      <ul className="flex flex-col gap-1">
        {sectionsFor(role).map((section) => (
          <li key={section.id}>
            <NavLink
              to={section.path}
              end={section.path === '/'}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  'hover:bg-hover-surface',
                  isActive
                    ? 'bg-accent-muted font-medium text-foreground'
                    : 'text-muted-foreground',
                )
              }
            >
              {/*
                `aria-current="page"` is set by `NavLink` itself when the route matches, which is
                the only correct way to say "you are here": a colour alone says it to nobody who
                cannot see it, and restating it by hand here would be a second opinion about the
                same fact.
              */}
              <section.icon aria-hidden className="size-4 shrink-0" />
              <span>{t(`nav.${section.id}`)}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
